import { Injectable, signal, computed } from '@angular/core';
import type { EnvironmentProviders, Provider, Type } from '@angular/core';
import type { Route } from '@angular/router';
import { Plugin, PluginContext, PluginMetadata } from '../shared/plugin.interface';
import { ComponentSlotExtension, RouteSlotOverride } from '../shared/plugin-contributions';
import type { NavExtension } from '@tourfold/shared-kernels/navigation';

/**
 * A lazy loader that returns a component type promise — same signature as
 * Angular's `loadComponent` factory, so it can be passed directly to the router.
 */
export type ComponentLoader = () => Promise<Type<unknown>>;

/**
 * Central registry for managing web-app plugins.
 *
 * Plugins are registered + activated during APP_INITIALIZER based on the
 * flat `plugins` array from the `frontend-configuration` API.
 *
 * The registry aggregates contributions from all active plugins:
 * - Route slot overrides (component substitution or full route replacement)
 * - Additional routes (plugin-only pages)
 * - Nav extensions (sidebar items)
 * - Providers (DI tokens/services)
 * - Settings providers (DI tokens/services visible only in the settings area)
 * - Component slot overrides/extensions (within-component substitution)
 *
 * @example
 * ```typescript
 * // In APP_INITIALIZER:
 * const plugins = await pluginLoader.loadPlugins(['bw']);
 * registry.registerAll(plugins);
 * await registry.activateAll([{ tenantId: '...' }]);
 *
 * // In route config:
 * const override = registry.getRouteComponent('tours-planning');
 * ```
 */
@Injectable({ providedIn: 'root' })
export class PluginRegistry {
  // ── Internal state ───────────────────────────────────────────────────

  private readonly plugins = new Map<string, Plugin>();
  private readonly _activePlugins = signal<Plugin[]>([]);

  /** Route slot overrides: slotId → lazy component loader */
  private readonly routeComponents = new Map<string, ComponentLoader>();

  /** Full route config overrides: slotId → route config */
  private readonly routeConfigOverrides = new Map<string, Route>();

  /** Component slot overrides: slotId → component type */
  private readonly componentSlotOverrides = new Map<string, Type<unknown>>();

  /** Component slot extensions: slotId → component type[] */
  private readonly componentSlotExtensions = new Map<string, ComponentSlotExtension[]>();

  // ── Public signals ───────────────────────────────────────────────────

  public readonly activePlugins = this._activePlugins.asReadonly();
  public readonly activePluginIds = computed(() => this._activePlugins().map((p) => p.id));

  // ── Registration ─────────────────────────────────────────────────────

  register(plugin: Plugin): void {
    if (this.plugins.has(plugin.id)) {
      console.warn(`[PluginRegistry] Plugin '${plugin.id}' already registered, skipping`);
      return;
    }

    this.validatePlugin(plugin);

    if (plugin.dependencies?.length) {
      const missing = plugin.dependencies.filter((dep) => !this.plugins.has(dep));
      if (missing.length > 0) {
        throw new Error(
          `[PluginRegistry] Plugin '${plugin.id}' has missing dependencies: ${missing.join(', ')}`
        );
      }
    }

    this.plugins.set(plugin.id, plugin);

    try {
      plugin.onRegister?.();
    } catch (error) {
      console.error(`[PluginRegistry] Error in onRegister for plugin '${plugin.id}':`, error);
    }
  }

  registerAll(plugins: Plugin[]): void {
    plugins.forEach((p) => this.register(p));
  }

  // ── Activation ───────────────────────────────────────────────────────

  async activateAll(contexts: PluginContext[]): Promise<void> {
    await this.deactivateAll();

    const registered = Array.from(this.plugins.values());

    for (const [i, plugin] of registered.entries()) {
      const context = contexts[i] ?? { tenantId: '' };

      try {
        // Register route slot overrides
        for (const override of plugin.getRouteSlotOverrides?.() ?? []) {
          if (isFullRouteSlotOverride(override)) {
            this.routeConfigOverrides.set(override.slotId, override.route);
          } else {
            this.routeComponents.set(override.slotId, override.loadComponent);
          }
        }

        // Register component slot overrides
        for (const override of plugin.getComponentSlotOverrides?.() ?? []) {
          this.componentSlotOverrides.set(override.slotId, override.component);
        }

        // Register component slot extensions
        for (const ext of plugin.getComponentSlotExtensions?.() ?? []) {
          const existing = this.componentSlotExtensions.get(ext.slotId) ?? [];
          this.componentSlotExtensions.set(ext.slotId, [...existing, ext]);
        }

        await plugin.onActivate?.(context);
      } catch (error) {
        console.error(`[PluginRegistry] Error activating plugin '${plugin.id}':`, error);
      }
    }

    this._activePlugins.set(registered);
  }

  async deactivateAll(): Promise<void> {
    const current = this._activePlugins();
    if (current.length === 0) return;

    for (const plugin of current) {
      try {
        await plugin.onDeactivate?.();
      } catch (error) {
        console.error(`[PluginRegistry] Error deactivating plugin '${plugin.id}':`, error);
      }
    }

    this.routeComponents.clear();
    this.routeConfigOverrides.clear();
    this.componentSlotOverrides.clear();
    this.componentSlotExtensions.clear();
    this._activePlugins.set([]);
  }

  // ── Route slot overrides ─────────────────────────────────────────────

  registerRouteComponent(key: string, loader: ComponentLoader): void {
    this.routeComponents.set(key, loader);
  }

  getRouteComponent(key: string): ComponentLoader | undefined {
    return this.routeComponents.get(key);
  }

  // ── Aggregated contributions from active plugins ─────────────────────

  getActiveNavExtensions(): NavExtension[] {
    return this._activePlugins().flatMap((p) => p.getNavExtensions?.() ?? []);
  }

  getActiveHiddenNavPaths(): string[] {
    return Array.from(new Set(this._activePlugins().flatMap((p) => p.getHiddenNavPaths?.() ?? [])));
  }

  getActiveSectionOrder(): string[] {
    return Array.from(new Set(this._activePlugins().flatMap((p) => p.getSectionOrder?.() ?? [])));
  }

  getActiveHidesCustomObjectsNav(): boolean {
    return this._activePlugins().some((p) => p.hidesCustomObjectsNav?.() ?? false);
  }

  getActiveRouteSlotOverrides(): RouteSlotOverride[] {
    return [
      ...Array.from(this.routeComponents.entries()).map(([slotId, loadComponent]) => ({
        slotId,
        loadComponent,
      })),
      ...Array.from(this.routeConfigOverrides.entries()).map(([slotId, route]) => ({
        slotId,
        route,
      })),
    ];
  }

  getActiveAdditionalRoutes(): Route[] {
    return this._activePlugins().flatMap((p) => p.getAdditionalRoutes?.() ?? []);
  }

  getActiveProviders(): Array<Provider | EnvironmentProviders> {
    return this._activePlugins().flatMap((p) => p.getProviders?.() ?? []);
  }

  getActiveSettingsProviders(): Array<Provider | EnvironmentProviders> {
    return this._activePlugins().flatMap((p) => p.getSettingsProviders?.() ?? []);
  }

  // ── Component slot resolution ────────────────────────────────────────

  resolveSlot(slotId: string): Type<unknown> | undefined {
    return this.componentSlotOverrides.get(slotId);
  }

  resolveExtensions(slotId: string): ComponentSlotExtension[] {
    return this.componentSlotExtensions.get(slotId) ?? [];
  }

  // ── Metadata ─────────────────────────────────────────────────────────

  getPlugin<T extends Plugin = Plugin>(pluginId: string): T | undefined {
    return this.plugins.get(pluginId) as T | undefined;
  }

  isPluginActive(pluginId: string): boolean {
    return this.activePluginIds().includes(pluginId);
  }

  getAllPluginMetadata(): PluginMetadata[] {
    return Array.from(this.plugins.values()).map((p) => ({
      id: p.id,
      name: p.name,
      version: p.version,
      description: p.description,
      dependencies: p.dependencies,
    }));
  }

  // ── Validation ───────────────────────────────────────────────────────

  private validatePlugin(plugin: Plugin): void {
    if (!plugin.id || typeof plugin.id !== 'string') {
      throw new Error('[PluginRegistry] Plugin must have a valid "id" string');
    }
    if (!plugin.name || typeof plugin.name !== 'string') {
      throw new Error(`[PluginRegistry] Plugin '${plugin.id}' must have a valid "name" string`);
    }
    if (!plugin.version || typeof plugin.version !== 'string') {
      throw new Error(`[PluginRegistry] Plugin '${plugin.id}' must have a valid "version" string`);
    }
  }
}

type FullRouteSlotOverride = Extract<RouteSlotOverride, { readonly route: Route }>;

function isFullRouteSlotOverride(override: RouteSlotOverride): override is FullRouteSlotOverride {
  return 'route' in override;
}
